import { ScheduledMeeting } from "@services/meetings/types";
import {
  displayDate,
  displayDateFromIso,
  displayMonth,
  displayMonthDay,
  displayTime,
} from "./localTime.utils";
import { TFunction } from "react-i18next";
import { DateTime } from "luxon";

export type DayValues = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";
export type FreqValues = "weekly" | "daily" | "monthly" | "yearly";
export type ByTypeValues = "DAY" | "MONTHDAY" | "BYDAY" | "BYMONTH" | "YEARLY";
export type RecurrenceByType = {
  type: ByTypeValues;
  values: DayValues[];
};
export type RecurrenceObj = Partial<ScheduledMeeting> &
  Partial<ICalendarRecurrenceObject>;

export interface ICalendarRecurrenceObject {
  freq: FreqValues;
  interval?: number;
  by?: RecurrenceByType[] | RecurrenceByType;
  dates?: unknown;
  times?: unknown;
  periods?: unknown;
  until?: string;
  count?: number;
  weekStart?: number | null;
  timeZone?: string;
}

export const dayOrder: DayValues[] = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
export const mappedDayOrder: { [key in DayValues]: number } = {
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
  SU: 7,
};

const freqParser = (
  freq: string
): Pick<ICalendarRecurrenceObject, "freq"> | undefined => {
  switch (freq) {
    case "DAILY":
      return { freq: "daily" };
    case "WEEKLY":
      return { freq: "weekly" };
    case "MONTHLY":
      return { freq: "monthly" };
    case "YEARLY":
      return { freq: "yearly" };
    default:
      return;
  }
};
const bydayParser = (byday: string): RecurrenceByType => ({
  type: "DAY",
  values: byday.split(",") as DayValues[],
});

export const parseRecurrence = (
  recurrence: string
): ICalendarRecurrenceObject | undefined => {
  if (!recurrence.startsWith("RRULE:FREQ=")) {
    console.log(`Recurrence Format not supported: "${recurrence}"`);
    return;
  }
  let obj: Partial<ICalendarRecurrenceObject> = { by: [] };
  recurrence
    .replace("RRULE:", "")
    .split(";")
    .forEach((item) => {
      const [key, value] = item.split("=");
      switch (key) {
        case "FREQ":
          let freq = freqParser(value);
          if (freq !== undefined) obj = { ...obj, ...freq };
          break;
        case "INTERVAL":
          obj.interval = value ? parseInt(value) : undefined;
          break;
        case "UNTIL":
          obj.until = value ? value : undefined;
          break;
        case "COUNT":
          obj.count = value ? parseInt(value) : undefined;
          break;
        case "DAY":
          obj.by = [
            ...(Array.isArray(obj.by) ? obj.by : obj.by ? [obj.by] : []),
            bydayParser(value),
          ];
          break;
        case "BYDAY":
          obj.by = {
            type: "BYDAY",
            values: value.split(",") as DayValues[],
          };
          break;
        case "BYMONTHDAY":
          obj.by = {
            type: "MONTHDAY",
            values: value.split(",") as DayValues[],
          };
        case "BYMONTH":
          obj.by = {
            type: "BYMONTH",
            values: value.split(",") as DayValues[],
          };
      }
    });
  return obj as ICalendarRecurrenceObject;
};

export const getDaysFromRecurrence = (
  recurrenceRule: string | undefined
): DayValues[] | undefined => {
  const recurrence =
    recurrenceRule !== undefined ? parseRecurrence(recurrenceRule) : undefined;
  if (recurrence?.freq === "daily") return dayOrder;
  const by =
    recurrence === undefined
      ? undefined
      : Array.isArray(recurrence.by)
      ? recurrence.by
      : [recurrence.by];
  return by?.[0]?.values;
};
export const stringifyRecurrence = (
  recurrence: ICalendarRecurrenceObject | undefined
): string => {
  if (!recurrence) {
    return "";
  }
  let recur = { ...recurrence };
  let def = "";
  if (recur.dates) {
    // def = "RDATE;VALUE=DATE:";
    // if (!Array.isArray(recur.dates)) {
    //   recur.dates = [recur.dates];
    // }
    // for (var i = 0; i < recur.dates.length; i++) {
    //   def += (i > 0 ? "," : "") + $.icalendar.formatDate(recur.dates[i]);
    // }
  } else if (recur.times) {
    // def = "RDATE;VALUE=DATE-TIME:";
    // if (!$.isArray(recur.times)) {
    //   recur.times = [recur.times];
    // }
    // for (var i = 0; i < recur.times.length; i++) {
    //   def += (i > 0 ? "," : "") + $.icalendar.formatDateTime(recur.times[i]);
    // }
  } else if (recur.periods) {
    // def = "RDATE;VALUE=PERIOD:";
    // if (!$.isArray(recur.periods[0])) {
    //   recur.periods = [recur.periods];
    // }
    // for (var i = 0; i < recur.periods.length; i++) {
    //   def +=
    //     (i > 0 ? "," : "") +
    //     $.icalendar.formatDateTime(recur.periods[i][0]) +
    //     "/" +
    //     (recur.periods[i][1].constructor != Date
    //       ? recur.periods[i][1]
    //       : $.icalendar.formatDateTime(recur.periods[i][1]));
    // }
  } else {
    def =
      "RRULE:FREQ=" +
      (recur.freq || "daily").toUpperCase() +
      (recur.interval ? ";INTERVAL=" + recur.interval : "") +
      // (recur.until
      //   ? ";UNTIL=" + $.icalendar.formatDateTime(recur.until)
      //   : recur.count
      //   ? ";COUNT=" + recur.count
      //   : "") +
      (recur.weekStart != null
        ? ";WKST=" + ["SU", "MO", "TU", "WE", "TH", "FR", "SA"][recur.weekStart]
        : "");
    if (recur.by) {
      (Array.isArray(recur.by) ? recur.by : [recur.by]).forEach(
        ({ values, type }) => {
          values = Array.isArray(values) ? values : [values];
          def = `${def};BY${type.toUpperCase()}=${values.join(",")}`;
        }
      );
    }
  }
  return def;
};

export const getRecurrenceValue = (
  t: TFunction,
  scheduledMeeting?: ScheduledMeeting,
  isMeetingException?: boolean,
  isTooltipText?: boolean,
  forUntilText?: boolean
) => {
  let tmp: RecurrenceObj = {};
  if (scheduledMeeting) {
    tmp = {
      start: scheduledMeeting.start,
      end: scheduledMeeting.end,
      ...parseRecurrence(scheduledMeeting.recurrence ?? ""),
    };
    return getRecurrenceText(
      t,
      tmp,
      isMeetingException,
      isTooltipText,
      forUntilText,
      scheduledMeeting?.timeZone
    );
  }
  return "";
};

export const getRecurrenceText = (
  t: TFunction,
  recurrenceObj: RecurrenceObj | undefined,
  isMeetingException?: boolean,
  isTooltipText?: boolean,
  forUntilText?: boolean,
  timeZone?: string
) => {
  if (!recurrenceObj) return "";

  const { freq, interval, by, until, start, count, end } = recurrenceObj;

  const dayMap: { [key: string]: string } = {
    SU: "Sunday",
    MO: "Monday",
    TU: "Tuesday",
    WE: "Wednesday",
    TH: "Thursday",
    FR: "Friday",
    SA: "Saturday",
  };

  const getStringDay = (day: string) => dayMap[day] || "";

  const getDaysOfWeek = (days: string[]) => days.map(getStringDay).join(", ");

  const getDayType = (day: string) => {
    const position = day.charAt(0);
    const dayName = getStringDay(day.slice(position === "-" ? 2 : 1));
    const prefixMap: { [key: string]: string } = {
      "1": "first",
      "2": "second",
      "3": "third",
      "4": "fourth",
      "-": "last",
    };
    return prefixMap[position] ? `${prefixMap[position]} ${dayName}` : "";
  };

  const getDayIndex = (abbr: DayValues) => {
    const days = {
      SU: 0,
      MO: 1,
      TU: 2,
      WE: 3,
      TH: 4,
      FR: 5,
      SA: 6,
    };
    return days[abbr];
  };

  const getFirstOccurenceDate = () => {
    const firstOccurrence = new Date();

    const frequencyHandlers: { [key in FreqValues]: () => Date } = {
      weekly: handleWeeklyOccurrence,
      monthly: handleMonthlyOccurrence,
      yearly: handleYearlyOccurrence,
      daily: () => firstOccurrence,
    };

    return frequencyHandlers[freq || "daily"]();
  };

  const handleWeeklyOccurrence = (): Date => {
    if (by && by.values) {
      const dayValues = Array.isArray(by.values) ? by.values : [by.values];
      const dates = dayValues.map((day) =>
        calculateNextDateForDay(new Date(), day as DayValues)
      );
      return new Date(Math.min(...dates));
    }
    return new Date();
  };

  const handleMonthlyOccurrence = (): Date => {
    if (!by) return new Date();

    if (!Array.isArray(by)) {
      const values = Array.isArray(by.values) ? by.values : [by.values];
      return getFirstOccurrenceForValues(values);
    }

    return calculateMonthlyDate(by);
  };

  const handleYearlyOccurrence = (): Date => {
    if (!by) return new Date();

    if (Array.isArray(by)) {
      return calculateYearlyDate(by);
    }

    return new Date();
  };

  const calculateNextDateForDay = (
    startDate: Date,
    dayCode: DayValues
  ): number => {
    const date = new Date(startDate.getTime());
    const dayIndex = getDayIndex(dayCode);
    const diff = dayIndex - date.getDay();
    date.setDate(date.getDate() + (diff >= 0 ? diff : diff + 7));
    return date.getTime();
  };

  const getFirstOccurrenceForValues = (values: string[]): Date => {
    const firstOccurrence = new Date();
    const dates = values.map((value) => {
      let date = new Date(firstOccurrence.getTime());
      date.setDate(Number(value));
      if (date < firstOccurrence) {
        date.setMonth(date.getMonth() + 1);
      }
      return date;
    });
    return dates.sort((a, b) => a.getTime() - b.getTime())[0];
  };

  const calculateMonthlyDate = (by: RecurrenceByType[]): Date => {
    let firstOccurrence = new Date();
    let times = 0;
    let dayIndex = -1;

    by.forEach((byItem: RecurrenceByType) => {
      if (byItem.type === "DAY") {
        const valuesStr = Array.isArray(byItem.values)
          ? byItem.values[0]
          : byItem.values;
        const prefix = valuesStr.charAt(0);
        if (prefix === "-") {
          times = -1;
          dayIndex = getDayIndex(valuesStr.slice(2) as DayValues);
        } else {
          times = parseInt(prefix) - 1;
          dayIndex = getDayIndex(valuesStr.slice(1) as DayValues);
        }
      }
    });

    if (dayIndex !== -1) {
      firstOccurrence = getAdjustedDate(
        firstOccurrence,
        dayIndex,
        times,
        times === -1
      );
    }

    return firstOccurrence;
  };

  const getAdjustedDate = (
    date: Date,
    dayIndex: number,
    times: number,
    adjustEndOfMonth: boolean = false
  ): Date => {
    date.setDate(1);
    if (adjustEndOfMonth) {
      date.setMonth(date.getMonth() + 1);
      date.setDate(0);
    }

    let diff = dayIndex - date.getDay();
    if (adjustEndOfMonth && diff > 0) diff -= 7;
    if (!adjustEndOfMonth && diff < 0) diff += 7;

    date.setDate(date.getDate() + diff + times * 7);
    return date;
  };

  const calculateYearlyDate = (by: RecurrenceByType[]) => {
    let firstOccurrence = new Date();
    let monthIndex = 0;
    let times = 0;
    let dayIndex = -1;
    by.forEach((byItem: RecurrenceByType) => {
      if (byItem.type === "BYMONTH") {
        monthIndex = Array.isArray(byItem.values)
          ? Number(byItem.values[0]) - 1
          : Number(byItem.values) - 1;
      } else if (byItem.type === "DAY") {
        const dayValue = Array.isArray(byItem.values)
          ? byItem.values[0]
          : byItem.values;
        const prefix = dayValue.charAt(0);
        if (dayValue.length === 2) {
          dayIndex = -1;
        } else {
          dayIndex = getDayIndex(dayValue.slice(1) as DayValues);
          if (prefix === "-") {
            times = -1;
          } else {
            times = parseInt(prefix);
          }
        }
      }
    });
    let date = new Date(firstOccurrence.getTime());
    date.setMonth(monthIndex);
    date.setDate(1);
    if (dayIndex > -1) {
      if (times < 0) {
        date.setMonth(date.getMonth() + 1);
        date.setDate(0);
        let diff = dayIndex - date.getDay();
        if (diff > 0) diff -= 7;
        date.setDate(date.getDate() + diff);
      } else {
        let diff = dayIndex - date.getDay();
        if (diff < 0) diff += 7;
        date.setDate(date.getDate() + diff + (times - 1) * 7);
      }
    }
    if (date < firstOccurrence) {
      date.setFullYear(date.getFullYear() + 1);
    }
    return date;
  };

  const getLastOccurrenceDate = () => {
    let until: Date | null = null;
    switch (freq) {
      case "daily":
        if (count) {
          until = new Date(start ?? "");
          until.setDate(
            until.getDate() + (Number(count) - 1) * Number(interval)
          );
        }
        break;
      case "weekly":
        if (Number(interval) === 1 && count) {
          until = getFirstOccurenceDate() ?? null;
          const dayIndices =
            (by?.values as DayValues[])?.map((day: DayValues) =>
              getDayIndex(day)
            ) || [];
          let countValue = Number(count);
          if (dayIndices.length > 1) {
            let dayIndex =
              dayIndices.find((index) => index === until?.getDay()) ||
              dayIndices[0];
            let currentDay = dayIndices.indexOf(dayIndex);
            const advanceToNextOccurrence = () => {
              dayIndex = dayIndices[currentDay];
              until?.setDate(
                until.getDate() + ((dayIndex - until.getDay() + 7) % 7)
              );
              countValue--;
            };
            while (countValue > 0) {
              advanceToNextOccurrence();
              currentDay = (currentDay + 1) % dayIndices.length;
              if (currentDay === 0 && count > 0) {
                until?.setDate(until.getDate() + 7 - until.getDay());
              }
            }
          } else {
            until?.setDate(until.getDate() + (countValue - 1) * 7);
          }
        } else if (count) {
          until = getFirstOccurenceDate() ?? null;
          until?.setDate(
            until.getDate() + (Number(count) - 1) * Number(interval) * 7
          );
        }
      case "monthly":
        if (count) {
          until = getFirstOccurenceDate() ?? null;
          until?.setMonth(
            until.getMonth() + (Number(count) - 1) * Number(interval)
          );
        }
        break;
      case "yearly":
        if (count) {
          until = getFirstOccurenceDate() ?? null;
          until?.setFullYear(
            until.getFullYear() + (Number(count) - 1) * Number(interval)
          );
        }
      default:
        break;
    }
    return until?.getTime();
  };

  const getIntervalText = (freq: FreqValues) => {
    if (interval && interval > 1) {
      switch (freq) {
        case "daily":
          return `${interval} days`;
        case "weekly":
          return `${interval} weeks on`;
        case "monthly":
          return `${interval} months`;
        case "yearly":
          return `${interval} years on`;
        default:
          return "";
      }
    } else {
      switch (freq) {
        case "daily":
          return "day";
        case "monthly":
          return "month";
        default:
          return "";
      }
    }
  };

  const getUntilText = () => {
    if (isMeetingException) return "";

    const dateText = until
      ? displayDateFromIso(until, timeZone)
      : count
      ? displayDate(getLastOccurrenceDate())
      : "";
    return dateText
      ? isTooltipText
        ? t("until {{$1}}", {
            $1: dateText,
          })
        : dateText
      : "";
  };

  const getEffectiveDateText = () =>
    isTooltipText ? `effective ${displayDate(start)}` : "";

  const getMeetingTimeText = () =>
    isTooltipText ? `from ${displayTime(start)} to ${displayTime(end)}` : "";

  const recurrenceTemplates = {
    daily: () =>
      forUntilText
        ? getUntilText()
        : isMeetingException
        ? t("Meeting Exception - {{$2}} {{$3}}", {
            $2: getEffectiveDateText(),
            $3: getMeetingTimeText(),
          })
        : t("Every {{$1}} {{$2}} {{$3}} {{$4}}", {
            $1: getIntervalText("daily"),
            $2: getEffectiveDateText(),
            $3: isTooltipText && getUntilText(),
            $4: getMeetingTimeText(),
          }),
    weekly: () => {
      const days = by ? (Array.isArray(by) ? by[0].values : by.values) : [];
      return forUntilText
        ? getUntilText()
        : isMeetingException
        ? t("Meeting Exception - {{$2}} {{$3}}", {
            $2: getEffectiveDateText(),
            $3: getMeetingTimeText(),
          })
        : t("Every {{$1}} {{$2}} {{$3}} {{$4}} {{$5}}", {
            $1: getIntervalText("weekly"),
            $2: getDaysOfWeek(days),
            $3: getEffectiveDateText(),
            $4: isTooltipText && getUntilText(),
            $5: getMeetingTimeText(),
          });
    },
    monthly: () => {
      const isMonthDay = !Array.isArray(by) && by?.type === "MONTHDAY";
      const isByMonth = !Array.isArray(by) && by?.type === "BYMONTH";
      const dayValues = by
        ? Array.isArray(by)
          ? by[0].values
          : by.values
        : [];
      const dayWithType =
        isMonthDay || isByMonth ? "" : getDayType(dayValues[0]);
      return isMonthDay || isByMonth
        ? forUntilText
          ? getUntilText()
          : isMeetingException
          ? t("Meeting Exception - {{$2}} {{$3}}", {
              $1: "Meeting Exception - ",
              $2: getEffectiveDateText(),
              $3: getMeetingTimeText(),
            })
          : t("Day {{$1}} of every {{$2}} {{$3}} {{$4}} {{$5}}", {
              $1: dayValues[0],
              $2: getIntervalText("monthly"),
              $3: getEffectiveDateText(),
              $4: isTooltipText && getUntilText(),
              $5: getMeetingTimeText(),
            })
        : forUntilText
        ? getUntilText()
        : isMeetingException
        ? t("Meeting Exception - {{$2}} {{$3}}", {
            $2: getEffectiveDateText(),
            $3: getMeetingTimeText(),
          })
        : t("Every {{$1}} of every {{$2}} {{$3}} {{$4}} {{$5}}", {
            $1: dayWithType || getDaysOfWeek(dayValues),
            $2: getIntervalText("monthly"),
            $3: getEffectiveDateText(),
            $4: isTooltipText && getUntilText(),
            $5: getMeetingTimeText(),
          });
    },
    yearly: () => {
      const isByDay = !Array.isArray(by) && by?.type === "BYDAY";
      const isEmpty = by && Array.isArray(by) && by?.length === 0;
      const dayValues = () => {
        if (!by || isEmpty) return [];
        return Array.isArray(by)
          ? by[0].values.length
            ? by[0].values
            : []
          : by.values;
      };
      const dayWithType =
        dayValues().length > 0 ? getDayType(dayValues()[0]) : "";
      return isByDay && dayValues().length > 0
        ? forUntilText
          ? t("{{$1}}", { $1: getUntilText() })
          : isMeetingException
          ? t("{{$1}} {{$2}} {{$3}}", {
              $1: "Meeting Exception",
              $2: getEffectiveDateText(),
              $3: getMeetingTimeText(),
            })
          : t("Every {{$1}} {{$2}} of {{$3}} {{$4}} {{$5}} {{$6}}", {
              $1: getIntervalText("yearly"),
              $2: dayWithType,
              $3: displayMonth(start),
              $4: getEffectiveDateText(),
              $5: isTooltipText && getUntilText(),
              $6: getMeetingTimeText(),
            })
        : forUntilText
        ? t("{{$1}}", { $1: getUntilText() })
        : isMeetingException
        ? t("{{$1}} {{$2}} {{$3}}", {
            $1: "Meeting Exception",
            $2: getEffectiveDateText(),
            $3: getMeetingTimeText(),
          })
        : t("Every {{$1}} {{$2}} {{$3}} {{$4}} {{$5}}", {
            $1: getIntervalText("yearly"),
            $2: displayMonthDay(start),
            $3: getEffectiveDateText(),
            $4: isTooltipText && getUntilText(),
            $5: getMeetingTimeText(),
          });
    },
  };

  return recurrenceTemplates[freq as FreqValues]?.() || "";
};
